import { createSlice } from '@reduxjs/toolkit';
import { isParsedEvent, workspaceKeyFromScope } from '../lib/packingListWire';

/**
 * PL 화면이 서버에서 받아 온 상태. 저장은 전부 서버가 하고, 여기에는 House별 마지막 조회 결과와
 * "다시 조회하라"는 알림 횟수를 둔다. House 키는 workspaceKey(tab, houseId)다.
 * 조회 결과를 House별로 남겨 두므로 패널을 닫았다 다시 열면 이전 화면이 바로 보이고, 그 뒤 서버 값으로 갱신된다.
 */
const initialState = {
    workspaces: {},
    changeSeq: {},
    uomMaster: [],
    uomAliases: {},
    partyAliases: {},
    lastConfirm: null,
    /** 이 화면이 파싱을 접수해 결과를 기다리는 문서. 문서 id → { key, filename }. */
    parseWatch: {},
    /** 기다리던 파싱이 끝났다는 알림. 업로드 창이 열려 있으면 그 창이, 아니면 작업 영역이 한 번 안내하고 지운다. */
    parsedNotice: null,
};

/** requestId는 마지막으로 보낸 조회, loadedRequestId는 마지막으로 반영한 조회의 번호다. data는 fromWorkspace로 바꾼 화면 모양이다. */
const emptyWorkspace = () => ({ status: 'idle', data: null, error: null, errorCode: null, requestId: 0, loadedRequestId: 0 });

/**
 * 같은 House를 여러 화면(작업 영역·업로드 창)이 따로 읽으므로, 늦게 도착한 옛 응답은 House 단위로 가려 버린다.
 * 번호는 화면과 무관하게 계속 커지므로 마지막으로 보낸 조회보다 오래된 응답이면 반영하지 않는다.
 */
const isLatest = (ws, requestId) => Boolean(ws) && requestId === ws.requestId;

/** 이미 보고 있는 것보다 오래된 알림인지. 오래된 알림으로 화면을 되돌리지 않는다. */
const isStaleChange = (data, event) => {
    if (!data) return false;
    // 파싱 결과 알림(packing_list:parsed)은 문서 version이 안 올라도 새 소식이다 — 파싱 대기 화면이 이걸 보고 다시 읽는다.
    if (event.parse_status != null || event.parse_result != null) return false;
    if (Number(event.allocation_version ?? 0) > Number(data.allocationVersion ?? 0)) return false;
    const known = Object.fromEntries((data.documents ?? []).map((d) => [d.id, Number(d.version ?? 0)]));
    const versions = event.document_versions ?? [];
    if (versions.some((v) => !(v.id in known) || Number(v.version) > known[v.id])) return false;
    // 삭제·폐기는 버전 목록 없이 document_ids만 온다. 아직 보이는 문서가 사라졌다면 새 소식이다.
    return !(event.document_ids ?? []).some((id) => id in known && !versions.some((v) => v.id === id));
};

const packingListSlice = createSlice({
    name: 'packingList',
    initialState,
    reducers: {
        workspaceRequested(state, { payload }) {
            const ws = state.workspaces[payload.key] ?? emptyWorkspace();
            ws.status = ws.data ? 'refreshing' : 'loading';
            ws.requestId = payload.requestId;
            state.workspaces[payload.key] = ws;
        },
        workspaceLoaded(state, { payload }) {
            const ws = state.workspaces[payload.key];
            if (!isLatest(ws, payload.requestId)) return;
            Object.assign(ws, { status: 'ready', data: payload.data, error: null, errorCode: null, loadedRequestId: payload.requestId });
        },
        /** 조회 실패. 이전에 받은 내용은 그대로 두어 화면이 비지 않게 한다. */
        workspaceFailed(state, { payload }) {
            const ws = state.workspaces[payload.key];
            if (!isLatest(ws, payload.requestId)) return;
            Object.assign(ws, { status: 'error', error: payload.error, errorCode: payload.code ?? null, loadedRequestId: payload.requestId });
        },
        /** 팀원의 변경 알림(packing_list:changed·parsed). 해당 House를 보고 있는 화면이 재조회한다. */
        packingListChanged(state, { payload }) {
            const key = workspaceKeyFromScope(payload?.scope);
            const event = payload ?? {};
            // 내가 접수한 파싱의 결과는 재조회 신호와 별개로 한 번 안내한다. 다른 문서를 검수 중이면 화면을 바꾸지 않고 알리기만 한다.
            const watched = isParsedEvent(event) ? state.parseWatch[event.id] : null;
            // 다시 접수한 뒤 늦게 온 이전 세대의 결과는 기다리던 결과가 아니다.
            if (watched && !(watched.generation != null && event.parse_generation != null && Number(event.parse_generation) < Number(watched.generation))) {
                delete state.parseWatch[event.id];
                state.parsedNotice = { docId: event.id, key: watched.key, filename: watched.filename, parseResult: event.parse_result ?? null, parseStatus: event.parse_status ?? null, version: event.version ?? null, seq: (state.parsedNotice?.seq ?? 0) + 1 };
            }
            if (isStaleChange(state.workspaces[key]?.data, event)) return;
            state.changeSeq[key] = (state.changeSeq[key] ?? 0) + 1;
        },
        /** 파싱 접수가 받아들여진 문서를 기억해 둔다. 세대(generation)를 같이 두어 그 접수의 결과만 받는다. */
        parseWatched(state, { payload }) {
            state.parseWatch[payload.docId] = { key: payload.key, filename: payload.filename ?? null, generation: payload.generation ?? null };
        },
        clearParsedNotice(state) {
            state.parsedNotice = null;
        },
        uomMasterLoaded(state, { payload }) {
            state.uomMaster = payload;
        },
        /** 서버가 단위 별칭을 저장한 뒤에만 부른다. 같은 표기를 이 화면에서 다시 묻지 않게 한다. */
        uomAliasSaved(state, { payload }) {
            state.uomAliases[String(payload.raw).trim().toUpperCase()] = payload.code;
        },
        /** 서버가 거래처 별칭을 저장한 뒤에만 부른다. */
        partyAliasSaved(state, { payload }) {
            const list = state.partyAliases[payload.tradePartnerId] ?? [];
            if (!list.includes(payload.alias)) state.partyAliases[payload.tradePartnerId] = [...list, payload.alias];
        },
        /** 확정 직후 작업 영역에 띄울 안내. 미배분 수는 재조회한 품목으로 계산한다. 재확정이면 동의한 영향 요약(editImpact)을 함께 둔다. */
        confirmSucceeded(state, { payload }) {
            state.lastConfirm = { key: payload.key, documentId: payload.documentId, editImpact: payload.editImpact ?? null };
        },
        clearLastConfirm(state) {
            state.lastConfirm = null;
        },
    },
});

export const {
    workspaceRequested,
    workspaceLoaded,
    workspaceFailed,
    packingListChanged,
    parseWatched,
    clearParsedNotice,
    uomMasterLoaded,
    uomAliasSaved,
    partyAliasSaved,
    confirmSucceeded,
    clearLastConfirm,
} = packingListSlice.actions;
export default packingListSlice.reducer;
